import {
  isLlmChatUiActionResolved,
  LlmChatMessageDto,
  LlmChatMessageRole,
  LlmChatUiActionType,
} from '@paladin-monorepo/common';
import { Bot, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import { LlmChatToolCallsSummary } from './LlmChatToolCallsSummary';
import { LlmChatMarkdownContent } from './LlmChatMarkdownContent';
import { LlmChatMessageAttachment } from './LlmChatMessageAttachment';
import { hasVisibleToolCalls } from './llm-chat-message-display';
import { LlmChatInquiryVersioningActions } from './LlmChatInquiryVersioningActions';
import { LlmChatAgentQuestionActions } from './LlmChatAgentQuestionActions';

interface LlmChatMessageBubbleProps {
  message: LlmChatMessageDto;
  allMessages: LlmChatMessageDto[];
  onSend?: (content: string) => void;
  actionsDisabled?: boolean;
}

export function LlmChatMessageBubble({
  message,
  allMessages,
  onSend,
  actionsDisabled,
}: LlmChatMessageBubbleProps) {
  const isUser = message.role === LlmChatMessageRole.User;
  const isAssistant = message.role === LlmChatMessageRole.Assistant;

  if (!isUser && !isAssistant) return null;
  if (
    isAssistant &&
    !message.content?.trim() &&
    message.attachments.length === 0 &&
    !hasVisibleToolCalls(message) &&
    !message.uiAction
  ) {
    return null;
  }

  return (
    <div className={cn('flex gap-3', isUser && 'flex-row-reverse')}>
      <div className={cn(
        'flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
        isUser ? 'bg-primary text-primary-foreground' : 'bg-muted',
      )}>
        {isUser ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
      </div>

      <div className={cn(
        'max-w-[75%] rounded-2xl px-4 py-2.5',
        isUser
          ? 'bg-primary text-primary-foreground'
          : 'bg-muted',
      )}>
        {message.content && (
          <LlmChatMarkdownContent
            content={message.content}
            sessionId={message.sessionId}
            attachments={message.attachments}
            references={message.references}
          />
        )}

        {hasVisibleToolCalls(message) && (
          <div className={message.content ? 'mt-3 border-t border-border/60 pt-3' : undefined}>
            <LlmChatToolCallsSummary toolCalls={message.toolCalls ?? []} />
          </div>
        )}

        {message.attachments.length > 0 && (
          <div className="mt-2 space-y-1">
            {message.attachments.map((att) => (
              <LlmChatMessageAttachment
                key={att.id}
                sessionId={message.sessionId}
                attachment={att}
                className="opacity-80"
              />
            ))}
          </div>
        )}

        {message.uiAction?.type === LlmChatUiActionType.InquiryVersioningChoice && onSend && (
          <LlmChatInquiryVersioningActions
            uiAction={message.uiAction}
            disabled={actionsDisabled || isLlmChatUiActionResolved(message, allMessages)}
            onSelect={(choice) => onSend(choice)}
          />
        )}

        {message.uiAction?.type === LlmChatUiActionType.AgentQuestion && onSend && (
          <LlmChatAgentQuestionActions
            uiAction={message.uiAction}
            disabled={actionsDisabled || isLlmChatUiActionResolved(message, allMessages)}
            onSelect={(answer) => onSend(answer)}
          />
        )}
      </div>
    </div>
  );
}
